import { useCallback, useRef, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { searchApiRef } from '@backstage/plugin-search-react';

export interface DocsSearchResult {
  title: string;
  text: string;
  location: string;
}

const DEBOUNCE_MS = 250;

export function useDocsSearch() {
  const searchApi = useApi(searchApiRef);
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<DocsSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const doSearch = useCallback(
    async (query: string) => {
      if (!query || query.length < 2) {
        setResults([]);
        setIsSearching(false);
        return;
      }

      try {
        const { results: searchResults } = await searchApi.query({
          term: query,
          types: ['docs-v2'],
          pageLimit: 20,
        });

        setResults(
          searchResults.map(r => ({
            title: r.document.title,
            text: r.document.text,
            location: r.document.location,
          })),
        );
      } catch {
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [searchApi],
  );

  const search = useCallback(
    (query: string) => {
      setTerm(query);
      if (!query || query.length < 2) {
        setResults([]);
        setIsSearching(false);
        return;
      }
      setIsSearching(true);

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => doSearch(query), DEBOUNCE_MS);
    },
    [doSearch],
  );

  const clear = useCallback(() => {
    setTerm('');
    setResults([]);
    setIsSearching(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return { term, results, isSearching, search, clear };
}
